import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { vendorAPI } from '../../services/api';
import { DashboardStats, RecentActivity, AnalyticsData, LeadTrendPoint } from '../../types';

interface DashboardState {
  stats: DashboardStats | null;
  recentActivities: RecentActivity | null;
  analytics: AnalyticsData | null;
  /** From dashboard endpoint; not overwritten by fetchAnalytics */
  leadTrends: LeadTrendPoint[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  stats: null,
  recentActivities: null,
  analytics: null,
  leadTrends: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchDashboardData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await vendorAPI.getDashboard();
      return (response.data as any).data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch dashboard data';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAnalytics = createAsyncThunk(
  'dashboard/fetchAnalytics',
  async (params: { period?: string; store?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await vendorAPI.getAnalytics(params);
      return (response.data as any).data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch analytics data';
      return rejectWithValue(errorMessage);
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard data
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats;
        state.recentActivities = action.payload.recentActivities;
        state.leadTrends = action.payload.analytics?.leadTrends ?? null;
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch analytics
      .addCase(fetchAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
        state.error = null;
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
